
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getSelectedProfileId } from '@/utils/geolocation';

export const useRecommendations = () => {
  const location = useLocation();
  const [session, setSession] = useState<any>(null);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [activeTab, setActiveTab] = useState<'food' | 'hospitals' | 'reels'>('food');
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  
  // Get score from location.state or localStorage (for direct access)
  const getScore = () => {
    if (location.state?.score !== undefined) {
      return location.state.score;
    }
    const storedScore = localStorage.getItem('lastAnemiaScore');
    return storedScore ? parseInt(storedScore) : 75; // Default score if none found
  };
  
  const getResultCategory = () => {
    if (location.state?.resultCategory !== undefined) {
      return location.state.resultCategory;
    }
    return localStorage.getItem('lastAnemiaCategory') || 'Riesgo moderado'; // Default category
  };
  
  const score = getScore();
  const resultCategory = getResultCategory();
  
  useEffect(() => {
    // Get current session
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      
      if (data.session) {
        fetchProfiles(data.session.user.id);
      }
    };
    
    getSession();
    
    // Cargar perfil seleccionado desde localStorage
    const savedProfileId = getSelectedProfileId();
    if (savedProfileId) {
      setSelectedProfileId(savedProfileId);
    }
    
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        if (newSession) {
          fetchProfiles(newSession.user.id);
        }
      }
    );
    
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);
  
  const fetchProfiles = async (userId: string) => {
    setIsLoadingProfiles(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Error fetching profiles:', error);
        toast('No se pudieron cargar los perfiles');
      } else {
        setProfiles(data || []);
        
        // Si no hay un perfil seleccionado, seleccionar el primero
        const savedProfileId = getSelectedProfileId();
        if (!savedProfileId && data && data.length > 0) {
          setSelectedProfileId(data[0].id);
        } else if (savedProfileId) {
          // Verificar si el perfil guardado existe en los perfiles cargados
          const profileExists = data?.some(profile => profile.id === savedProfileId);
          if (profileExists) {
            setSelectedProfileId(savedProfileId);
          } else if (data && data.length > 0) {
            // Si no existe, seleccionar el primero
            setSelectedProfileId(data[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Unexpected error:', error);
    } finally {
      setIsLoadingProfiles(false);
    }
  };
  
  return {
    session,
    profiles,
    isLoadingProfiles,
    score,
    resultCategory,
    activeTab,
    setActiveTab,
    selectedProfileId,
    setSelectedProfileId
  };
};
